import { useEffect, useState } from 'react';
import { joinCatalogue } from '../../domain/plants/plantCatalogue';
import { fetchCatalogue, fetchProse } from '../../infrastructure/content/plantContentClient';
import { useLocale } from '../i18n/LocaleProvider';

/**
 * The catalogue, joined to the prose of the active language.
 *
 * Cached at module scope and keyed by language, because the browse grid and the
 * detail page are separate routes: without this, opening a plant and pressing
 * back would refetch the whole catalogue and flash a loading state over content
 * the user was just looking at.
 */
const cache = new Map();

function loadCatalogue(languageCode) {
    if (!cache.has(languageCode)) {
        const pending = Promise.all([fetchCatalogue(), fetchProse(languageCode)])
            .then(([plants, { prose, fallbackProse }]) => joinCatalogue(plants, prose, fallbackProse))
            // A failed load must not poison the cache — the next mount should be
            // allowed to try again rather than replaying the rejection forever.
            .catch((error) => {
                cache.delete(languageCode);

                throw error;
            });

        cache.set(languageCode, pending);
    }

    return cache.get(languageCode);
}

export function usePlantCatalogue() {
    const { languageCode } = useLocale();
    const [state, setState] = useState({ status: 'loading', plants: [], error: null });

    useEffect(() => {
        let active = true;
        setState({ status: 'loading', plants: [], error: null });

        loadCatalogue(languageCode).then(
            (plants) => active && setState({ status: 'ready', plants, error: null }),
            (error) => active && setState({ status: 'failed', plants: [], error }),
        );

        // The locale can change under a mounted screen via the language
        // switcher, so a resolved fetch for the previous language must not land.
        return () => {
            active = false;
        };
    }, [languageCode]);

    return state;
}

/** Test seam — the module cache would otherwise leak one spec's catalogue into the next. */
export function clearPlantCatalogueCache() {
    cache.clear();
}
